import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Pressable, View, Platform, Linking, ScrollView } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import { Image } from 'expo-image';
import { useModalHelper } from '@/hooks/useModalHelper';

const CARD_WIDTH = 168;
const CARD_GAP = 10;
const SCROLL_STEP = CARD_WIDTH + CARD_GAP;

const createStyles = (colors) => StyleSheet.create({
  container: {
    marginTop: 10,
    marginBottom: 4,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    overflow: 'hidden',
  },
  track: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
  },
  scrollView: {
    width: '100%',
    maxWidth: '100%',
  },
  scrollContent: {
    gap: CARD_GAP,
    paddingRight: 8,
  },
  card: {
    width: CARD_WIDTH,
    flexShrink: 0,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
  },
  imageWrap: {
    width: '100%',
    height: 120,
    backgroundColor: colors.solid_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  body: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  title: {
    fontWeight: '600',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  buyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  disclosure: {
    marginTop: 6,
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_primary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    zIndex: 2,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      cursor: 'pointer',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 3,
    }),
  },
  navButtonLeft: {
    left: 4,
  },
  navButtonRight: {
    right: 4,
  },
});

const ProductCard = React.memo(({ product, colors, styles, onPress }) => {
  const hasRating = typeof product.rating === 'number' && product.rating > 0;

  return (
    <Pressable
      style={styles.card}
      onPress={() => onPress(product)}
      accessibilityRole="button"
      accessibilityLabel={`${product.title}${product.price ? `, ${product.price}` : ''}`}
      accessibilityHint="Opens the store page to buy this product"
    >
      <View style={styles.imageWrap}>
        {product.thumbnail ? (
          <Image
            source={{ uri: product.thumbnail }}
            style={styles.image}
            contentFit="contain"
            transition={120}
          />
        ) : (
          <Icon name="bag-shopping" size={28} color={colors.text_color_tertiary} />
        )}
      </View>
      <View style={styles.body}>
        <Text size="s" color="primary" numberOfLines={2} style={styles.title}>
          {product.title}
        </Text>
        {hasRating && (
          <View style={styles.ratingRow}>
            <Icon name="star" size={10} color={colors.text_color_secondary} />
            <Text size="xs" color="secondary" style={{ marginLeft: 4 }}>
              {product.rating.toFixed(1)}{product.reviews ? ` (${product.reviews})` : ''}
            </Text>
          </View>
        )}
        <View style={styles.priceRow}>
          {product.price ? (
            <Text size="n" color="primary" style={{ fontWeight: '700' }}>
              {product.price}
            </Text>
          ) : null}
        </View>
        <View style={styles.buyRow}>
          {product.source ? (
            <Text size="xs" color="tertiary" numberOfLines={1} style={{ flexShrink: 1 }}>
              {product.source}
            </Text>
          ) : null}
          <Icon
            name="arrow-up-right-from-square"
            size={10}
            color={colors.text_color_secondary}
            style={{ marginLeft: 'auto' }}
          />
        </View>
      </View>
    </Pressable>
  );
});
ProductCard.displayName = 'ProductCard';

const NavButton = React.memo(({ direction, onPress, colors, styles }) => (
  <Pressable
    onPress={onPress}
    style={[
      styles.navButton,
      direction === 'left' ? styles.navButtonLeft : styles.navButtonRight,
    ]}
    accessibilityRole="button"
    accessibilityLabel={direction === 'left' ? 'Previous products' : 'Next products'}
  >
    <Icon
      name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
      size={14}
      color={colors.text_color_primary}
    />
  </Pressable>
));
NavButton.displayName = 'NavButton';

function MessageProducts({ products = [] }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const scrollRef = useRef(null);
  const nativeOffsetRef = useRef(0);
  const nativeViewportRef = useRef(0);
  const nativeContentRef = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback((scrollLeft, clientWidth, scrollWidth) => {
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  const syncFromWebEl = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState(el.scrollLeft, el.clientWidth, el.scrollWidth);
  }, [updateScrollState]);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    // Measure after layout so we know whether arrows are needed.
    const id = requestAnimationFrame(syncFromWebEl);
    const el = scrollRef.current;
    if (!el) return () => cancelAnimationFrame(id);

    const onScroll = () => syncFromWebEl();
    el.addEventListener('scroll', onScroll, { passive: true });

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(syncFromWebEl);
      ro.observe(el);
    }
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [products, syncFromWebEl]);

  const syncNativeState = useCallback(() => {
    updateScrollState(
      nativeOffsetRef.current,
      nativeViewportRef.current,
      nativeContentRef.current,
    );
  }, [updateScrollState]);

  const onNativeScroll = useCallback((event) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    nativeOffsetRef.current = contentOffset.x;
    nativeViewportRef.current = layoutMeasurement.width;
    nativeContentRef.current = contentSize.width;
    updateScrollState(contentOffset.x, layoutMeasurement.width, contentSize.width);
  }, [updateScrollState]);

  const onNativeContentSizeChange = useCallback((contentWidth) => {
    nativeContentRef.current = contentWidth;
    syncNativeState();
  }, [syncNativeState]);

  const onNativeLayout = useCallback((event) => {
    nativeViewportRef.current = event.nativeEvent.layout.width;
    syncNativeState();
  }, [syncNativeState]);

  const scrollBy = useCallback((delta) => {
    if (Platform.OS === 'web') {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: delta, behavior: 'smooth' });
      // scroll event updates state; also sync shortly after for browsers that debounce.
      requestAnimationFrame(syncFromWebEl);
      return;
    }
    const maxOffset = Math.max(0, nativeContentRef.current - nativeViewportRef.current);
    const next = Math.min(maxOffset, Math.max(0, nativeOffsetRef.current + delta));
    scrollRef.current?.scrollTo({ x: next, animated: true });
    nativeOffsetRef.current = next;
    syncNativeState();
  }, [syncFromWebEl, syncNativeState]);

  const onProductPress = useCallback((product) => {
    const url = product?.link;
    if (!url) return;
    showConfirmationModal({
      title: 'External Link',
      message: 'You are about to visit an external store to buy this product. Please be careful with links from unknown sources.',
      itemName: product.source || url,
      confirmText: 'Open Link',
      cancelText: 'Cancel',
      onConfirm: () => {
        if (Platform.OS === 'web') {
          window.open(url, '_blank');
        } else {
          Linking.openURL(url);
        }
      },
    });
  }, [showConfirmationModal]);

  if (!Array.isArray(products) || products.length === 0) return null;

  const hasAffiliateLinks = products.some(p => p?.affiliated === true);

  const cards = products.map((product, index) => (
    <ProductCard
      key={product._id || product.link || index}
      product={product}
      colors={colors}
      styles={styles}
      onPress={onProductPress}
    />
  ));

  const disclosure = hasAffiliateLinks ? (
    <Text size="xs" color="tertiary" style={styles.disclosure}>
      Shopping results — Gab may earn a commission when you buy through these links.
    </Text>
  ) : null;

  const navButtons = (
    <>
      {canScrollLeft && (
        <NavButton
          direction="left"
          onPress={() => scrollBy(-SCROLL_STEP)}
          colors={colors}
          styles={styles}
        />
      )}
      {canScrollRight && (
        <NavButton
          direction="right"
          onPress={() => scrollBy(SCROLL_STEP)}
          colors={colors}
          styles={styles}
        />
      )}
    </>
  );

  // Web: width:0 + minWidth:100% prevents shrink-to-fit blowout from the cards.
  if (Platform.OS === 'web') {
    return (
      <div
        style={{
          display: 'block',
          width: 0,
          minWidth: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          marginTop: 10,
          marginBottom: 4,
          boxSizing: 'border-box',
        }}
        aria-label="Shopping results"
      >
        <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
          <div
            ref={scrollRef}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: CARD_GAP,
              overflowX: 'auto',
              overflowY: 'hidden',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              paddingBottom: 4,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              boxSizing: 'border-box',
            }}
          >
            {cards}
          </div>
          {navButtons}
        </div>
        {disclosure}
      </div>
    );
  }

  return (
    <View style={styles.container} accessibilityLabel="Shopping results">
      <View style={styles.track}>
        <ScrollView
          ref={scrollRef}
          horizontal
          nestedScrollEnabled
          showsHorizontalScrollIndicator={false}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          onScroll={onNativeScroll}
          onContentSizeChange={onNativeContentSizeChange}
          onLayout={onNativeLayout}
          scrollEventThrottle={16}
        >
          {cards}
        </ScrollView>
        {navButtons}
      </View>
      {disclosure}
    </View>
  );
}

MessageProducts.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    price: PropTypes.string,
    source: PropTypes.string,
    link: PropTypes.string,
    thumbnail: PropTypes.string,
    rating: PropTypes.number,
    reviews: PropTypes.number,
    affiliated: PropTypes.bool,
  })),
};

MessageProducts.displayName = 'MessageProducts';

export default React.memo(MessageProducts);
